import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DbCultureInfos } from "../../Core/Api/Types/db-culture-infos";
import { EnrichedMetadata } from "../../Core/Api/Types/enriched-metadata";
import FullTaskSplit from "../../Layouts/FullTask/FullTaskSplit";
import { useDispatch } from "react-redux";
import {
  CopyMetadata,
  CopyTitleMetadata,
  GetCulturesList,
  GetEditTitle,
  GetIsRightPanelOpen,
  GetMetadataMap,
  GetPackageCultures,
  GetPackageList,
  GetSelectedPackageIndex,
  GetTitleCultures,
  GetTitleSelectedCulture,
  HydrateMetadataFromTitle,
  SetIsRightPanelOpen,
  SetMetadataMap,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { useSelector } from "react-redux";
import {
  getAssetCultures,
  getBroacasterIdMetadata,
  getMetadataCultures,
} from "../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { assetService } from "../../Core/Api/services";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import LanguageIcon from "@mui/icons-material/Language";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import PanelHeader from "../../Components/Panels/PanelHeader/panel-header";
import { useTranslation } from "react-i18next";

const LOCALIZED_FIELD_TYPES = [
  BaseMetadataType.DbLocalizedTitlesMultiMetadataValueInfos,
  BaseMetadataType.DbLocalizedCreditsMultiMetadataValueInfos,
  BaseMetadataType.DbCastsMetadataValueInfos,
  BaseMetadataType.DbLocalizedBoolMultiMetadataValueInfos,
  BaseMetadataType.DbSynopsesMetadataValueInfos,
  BaseMetadataType.DbLocalizedDateTimeMultiMetadataValueInfos,
];
interface RightPanelBundlemakerProps {
  metadata: EnrichedMetadata[];
  titleLocalizedMeta: EnrichedMetadata[];
  selectedCultureId: number;
}

const RightPanelBundleMaker: React.FC<RightPanelBundlemakerProps> = (props: RightPanelBundlemakerProps) => {
  const { t } = useTranslation();
  const { metadata, titleLocalizedMeta, selectedCultureId } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const enqueueCustomSnackbar = useCustomSnackbar();

  //------Select store values------

  const packageList = useSelector(GetPackageList);
  const selectedPackageIndex = useSelector(GetSelectedPackageIndex);
  const cultures = useSelector(GetCulturesList);
  const packageMap = useSelector(GetMetadataMap);
  const isRightPanelOpen = useSelector(GetIsRightPanelOpen);
  const packageCultures = useSelector(GetPackageCultures);
  const editTitle = useSelector(GetEditTitle);
  const titleCultures = useSelector(GetTitleCultures);
  const selectedTitleCulture = useSelector(GetTitleSelectedCulture);

  //------local state------

  const [selectedCultures, setSelectedCultures] = useState<number[]>([]);
  const [selectedPackageCultures, setSelectedPackageCulture] = useState<DbCultureInfos[]>([]);
  const [selectedFields, setSelectedFields] = useState<number[]>([]);
  const [packageToCopy, setPackageToCopy] = useState<number>(-1);
  const [shouldCopy, setShouldCopy] = useState(false);

  const [selectedCulturesTitle, setSelectedCulturesTitle] = useState<number[]>([]);
  const [selectedFieldsTitle, setSelectedFieldsTitle] = useState<number[]>([]);

  // On current selected package change, set destination package to this package
  useEffect(() => {
    if (packageList && selectedPackageIndex > -1 && packageList[selectedPackageIndex]) {
      setPackageToCopy(packageList[selectedPackageIndex].id);
    }
  }, [packageList.length, selectedPackageIndex]);

  // On destination package selection change set available to selection cultures accordingly
  useEffect(() => {
    setSelectedPackageCulture(
      packageList[selectedPackageIndex]?.id === packageToCopy
        ? packageCultures
        : packageMap[packageToCopy] && packageMap[packageToCopy].length > 0
        ? getMetadataCultures(packageMap[packageToCopy], cultures)
        : getAssetCultures(packageList.find((x) => x.id === packageToCopy) || ({} as DbAssetObjectInfos), cultures)
    );
  }, [packageToCopy, packageCultures.length]);

  // on toggle mode (edit package / edit title) reset form
  useEffect(() => {
    setSelectedCulturesTitle([]);
    setSelectedFieldsTitle([]);
    setSelectedCultures([]);
    setSelectedFields([]);
  }, [editTitle]);

  // when currently selected culture changes filter it out from the form
  useEffect(() => {
    setSelectedCultures((x) => x.filter((y) => y !== selectedCultureId));
  }, [selectedCultureId]);

  useEffect(() => {
    setSelectedCultures((x) => x.filter((y) => y !== selectedTitleCulture?.id));
  }, [selectedTitleCulture?.id]);

  //------handle user actions------

  const handleFieldsChange = (event) => {
    const {
      target: { value },
    } = event;

    setSelectedFields(typeof value === "string" ? value.split(",") : value);
  };

  const handleFieldsTitleChange = (event) => {
    const {
      target: { value },
    } = event;

    setSelectedFieldsTitle(typeof value === "string" ? value.split(",") : value);
  };
  const handlePackageToCopyChange = (e) => {
    setSelectedCultures([]);
    setPackageToCopy(e.target.value);
  };

  const handleCopyMetadata = () => {
    if (packageMap[packageToCopy]?.length > 0) {
      dispatch(
        CopyMetadata({
          packageToCopy: packageToCopy,
          fieldIds: selectedFields,
          targetCultureIds: selectedCultures,
          sourceCultureId: selectedCultureId,
        })
      );
      setSelectedCultures([]);
      setSelectedFields([]);
    } else {
      const packageData = packageList.find((x) => x.id === packageToCopy);
      if (!packageData) return;
      const packageBroadcasterId = getBroacasterIdMetadata(packageData);
      const packageCultures = getAssetCultures(packageData, cultures);
      if (!packageBroadcasterId || !packageCultures) return;
      assetService
        .GetBundleMakerComputeCulturedMetadata({
          objectId: packageToCopy,
          broadcasterId: packageBroadcasterId,
          cultureIds: packageCultures.map((x) => x.id),
          cultureLanguageOverrides: null,
        })
        .then((response) => {
          if (response.success && response.metadata) {
            dispatch(SetMetadataMap({ packageId: packageToCopy, metadata: response.metadata }));
            setShouldCopy(true);
          } else {
            enqueueCustomSnackbar(response.message || t("alert:error.loadingDestinationPackageMetadata"), "error");
          }
        });
    }
  };

  const handleCopyTitleMetadata = () => {
    selectedTitleCulture?.id &&
      dispatch(
        CopyTitleMetadata({
          fieldIds: selectedFields,
          targetCultureIds: selectedCultures,
          sourceCultureId: selectedTitleCulture.id,
        })
      );
    setSelectedCultures([]);
    setSelectedFields([]);
  };

  const handleGetMetadataFromTitle = () => {
    dispatch(HydrateMetadataFromTitle({ selectedCulturesTitle, selectedFieldsTitle }));
    setSelectedCulturesTitle([]);
    setSelectedFieldsTitle([]);
  };

  // handle copy on destination package metadata reception
  useEffect(() => {
    if (shouldCopy) {
      dispatch(
        CopyMetadata({
          packageToCopy: packageToCopy,
          fieldIds: selectedFields,
          targetCultureIds: selectedCultures,
          sourceCultureId: selectedCultureId,
        })
      );
      setShouldCopy(false);
      setSelectedCultures([]);
      setSelectedFields([]);
    }
  }, [packageMap[packageToCopy]?.length, shouldCopy]);
  return (
    <FullTaskSplit
      sx={{
        width: isRightPanelOpen ? "auto" : "0px",
        minWidth: isRightPanelOpen ? "320px" : "0px",
        borderLeft: "1px solid rgba(194, 194, 214, 0.1)",
        marginTop: "64px",
        zIndex: 200,
        overflowY: "scroll",
        maxHeight: "calc(100vh - 70px)",
      }}
      layout="1:6"
    >
      <PanelHeader
        sx={{
          backgroundColor: theme.palette.mode === "light" ? theme.palette.background.paper : "auto",
          position: "relative",
          right: "32px",
          width: "320px",
          paddingTop: "4px",
          paddingBottom: "4px",
          paddingLeft: "12px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
        onClose={() => dispatch(SetIsRightPanelOpen(false))}
        title={t("page:panelHeader.title.copyMetadata")}
        children={
          ((editTitle && selectedTitleCulture && selectedTitleCulture.id >= 0) ||
            (!editTitle && selectedCultureId >= 0)) && (
            <Box sx={{ opacity: 0.7, height: 0, bottom: "8px" }} zIndex={2000} position={"relative"}>
              <Chip
                label={
                  editTitle
                    ? selectedTitleCulture!.englishName
                    : cultures.find((x) => x.id === selectedCultureId)?.englishName
                }
                color="info"
              />
            </Box>
          )
        }
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}>
        {!editTitle && (
          <>
            <InputLabel sx={{ marginBottom: "10px", marginTop: "10px" }} htmlFor="package-select">
              {t("field:label.selectDestinationPackage")}
            </InputLabel>
            <Select
              sx={{ width: "100%", marginBottom: "10px" }}
              onChange={handlePackageToCopyChange}
              value={packageToCopy}
              id="package-select"
            >
              {packageList
                .filter(
                  (x, index) =>
                    index === selectedPackageIndex ||
                    getAssetCultures(x, cultures).length !== 0 ||
                    (packageMap[x.id] && packageMap[x.id].length > 0)
                )
                .map((x, i) => (
                  <MenuItem key={i} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
            </Select>
          </>
        )}
        <InputLabel sx={{ marginBottom: "10px", marginTop: "10px" }} htmlFor="cultures-select">
          {t("field:label.selectDestinationCountries")}
        </InputLabel>
        {editTitle ? (
          <Autocomplete
            sx={{ marginBottom: "10px" }}
            value={selectedCultures}
            onChange={(e, newValue) => setSelectedCultures(newValue as number[])}
            multiple
            id="cultures-select"
            options={[...titleCultures]
              .sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))
              .filter((x) => x?.id !== selectedTitleCulture?.id)
              .map((x) => x?.id)}
            getOptionLabel={(option) => titleCultures?.find((x) => x?.id === option)?.englishName || ""}
            defaultValue={[]}
            filterSelectedOptions
            fullWidth
            renderInput={(params) => (
              <OutlinedInput
                rows={1}
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", overflowY: "scroll", maxHeight: "40px" }}
                placeholder={t("field:placeholder.selectCountries")}
              />
            )}
          />
        ) : (
          <Autocomplete
            sx={{ marginBottom: "10px" }}
            value={selectedCultures}
            onChange={(e, newValue) => setSelectedCultures(newValue as number[])}
            multiple
            id="cultures-select"
            options={[...selectedPackageCultures]
              .sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))
              .filter((x) => x.id !== selectedCultureId)
              .map((x) => x.id)}
            getOptionLabel={(option) => selectedPackageCultures.find((x) => x.id === option)?.englishName || ""}
            defaultValue={[]}
            filterSelectedOptions
            fullWidth
            renderInput={(params) => (
              <OutlinedInput
                rows={1}
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", overflowY: "scroll", maxHeight: "40px" }}
                placeholder={t("field:placeholder.selectCountries")}
              />
            )}
          />
        )}
        <Box
          sx={{
            marginBottom: "12px",
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "row",
            flexWrap: "nowrap",
          }}
        >
          {editTitle && (
            <Tooltip title={t("tooltip:title.selectCulturesWithSameLanguage")} placement="left">
              <IconButton
                sx={{ marginRight: "15px" }}
                onClick={() =>
                  setSelectedCultures(
                    titleCultures
                      .filter(
                        (x) =>
                          x.languagesMap &&
                          selectedTitleCulture?.languagesMap &&
                          x.languagesMap[0].id === selectedTitleCulture?.languagesMap[0].id &&
                          x.id !== selectedTitleCulture.id
                      )
                      .map((x) => x.id)
                  )
                }
              >
                <LanguageIcon />
              </IconButton>
            </Tooltip>
          )}
          <Link
            underline="hover"
            sx={{ marginRight: "15px" }}
            onClick={() =>
              editTitle
                ? setSelectedCultures(titleCultures.filter((x) => x.id !== selectedTitleCulture?.id).map((x) => x.id))
                : setSelectedCultures(
                    selectedPackageCultures.filter((x) => x.id !== selectedCultureId).map((x) => x.id)
                  )
            }
          >
            {t("common:selectAll")}
          </Link>
          <Link underline="hover" onClick={() => setSelectedCultures([])}>
            {t("common:unselectAll")}
          </Link>
        </Box>
        <InputLabel sx={{ marginBottom: "10px" }} htmlFor="fields-select">
          {t("field:label.selectFieldsToCopy")}
        </InputLabel>
        <Select
          id="fields-select"
          sx={{ width: "100%", marginBottom: "10px" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          onChange={handleFieldsChange}
          value={selectedFields}
          multiple
        >
          {editTitle
            ? titleLocalizedMeta
                .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                .map((x, i) => (
                  <MenuItem key={i} value={x.field?.id}>
                    {x.field?.label?.replace(/ *\[[^\]]*]/g, "")}
                  </MenuItem>
                ))
            : metadata
                .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                .map((x, i) => (
                  <MenuItem key={i} value={x.field?.id}>
                    {x.field?.label?.replace(/ *\[[^\]]*]/g, "")}
                  </MenuItem>
                ))}
        </Select>
        <Box
          sx={{
            marginBottom: "12px",
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "row",
            flexWrap: "nowrap",
          }}
        >
          <Link
            underline="hover"
            sx={{ marginRight: "15px" }}
            onClick={() =>
              setSelectedFields(
                editTitle
                  ? titleLocalizedMeta
                      .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                      .map((x) => x.field!.id) || []
                  : metadata
                      .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                      .map((x) => x.field!.id) || []
              )
            }
          >
            {t("common:selectAll")}
          </Link>
          <Link underline="hover" onClick={() => setSelectedFields([])}>
            {t("common:unselectAll")}
          </Link>
        </Box>
        <Button
          disabled={
            selectedFields.length === 0 || selectedCultures.length === 0 || (editTitle && !selectedTitleCulture)
          }
          variant="contained"
          color="primary"
          fullWidth
          onClick={editTitle ? handleCopyTitleMetadata : handleCopyMetadata}
        >
          {t("common:copy")}
        </Button>
        {!editTitle && (
          <>
            <Divider
              orientation="horizontal"
              sx={{
                mt: 2,
                mb: 2,
                width: "100%",
              }}
            />
            <Typography variant="h5">{t("page:body.getMetadataFromTitle")}</Typography>
            <InputLabel sx={{ marginBottom: "10px", marginTop: "10px" }} htmlFor="cultures-select-title">
              {t("field:label.selectCountriesToHydrate")}
            </InputLabel>
            {
              <Autocomplete
                sx={{ marginBottom: "10px" }}
                value={selectedCulturesTitle}
                onChange={(e, newValue) => setSelectedCulturesTitle(newValue as number[])}
                multiple
                id="cultures-select-title"
                options={[...selectedPackageCultures.filter((x) => titleCultures.find((c) => c.id === x.id))]
                  .sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))
                  .map((x) => x.id)}
                getOptionLabel={(option) => selectedPackageCultures.find((x) => x.id === option)?.englishName || ""}
                defaultValue={[]}
                filterSelectedOptions
                fullWidth
                renderInput={(params) => (
                  <OutlinedInput
                    rows={1}
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%", overflowY: "scroll", maxHeight: "40px" }}
                    placeholder={t("field:placeholder.selectCountries")}
                  />
                )}
              />
            }
            <Box
              sx={{
                marginBottom: "12px",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                flexDirection: "row",
                flexWrap: "nowrap",
              }}
            >
              <Link
                underline="hover"
                sx={{ marginRight: "15px" }}
                onClick={() =>
                  setSelectedCulturesTitle(
                    selectedPackageCultures.filter((x) => titleCultures.find((c) => c.id === x.id)).map((x) => x.id)
                  )
                }
              >
                {t("common:selectAll")}
              </Link>
              <Link underline="hover" onClick={() => setSelectedCulturesTitle([])}>
                {t("common:unselectAll")}
              </Link>
            </Box>
            <InputLabel sx={{ marginBottom: "10px" }} htmlFor="fields-select-title">
              {t("field:label.selectFieldsToGetFromTitle")}
            </InputLabel>
            <Select
              id="fields-select-title"
              sx={{ width: "100%", marginBottom: "10px" }}
              onChange={handleFieldsTitleChange}
              value={selectedFieldsTitle}
              multiple
            >
              {metadata
                .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                .map((x, i) => (
                  <MenuItem key={i} value={x.field?.id}>
                    {x.field?.label?.replace(/ *\[[^\]]*]/g, "")}
                  </MenuItem>
                ))}
            </Select>
            <Box
              sx={{
                marginBottom: "12px",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                flexDirection: "row",
                flexWrap: "nowrap",
              }}
            >
              <Link
                underline="hover"
                sx={{ marginRight: "15px" }}
                onClick={() =>
                  setSelectedFieldsTitle(
                    metadata
                      .filter((x) => LOCALIZED_FIELD_TYPES.find((id) => id === x.field?.baseType))
                      .map((x) => x.field!.id) || []
                  )
                }
              >
                {t("common:selectAll")}
              </Link>
              <Link underline="hover" onClick={() => setSelectedFieldsTitle([])}>
                {t("common:unselectAll")}
              </Link>
            </Box>
            <Button
              disabled={selectedFieldsTitle.length === 0 || selectedCulturesTitle.length === 0}
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleGetMetadataFromTitle}
            >
              {t("button:hydrate")}
            </Button>
          </>
        )}
      </Box>
    </FullTaskSplit>
  );
};

export default RightPanelBundleMaker;
